import { HomeworkSchedule, SelectedItem, setting } from "shared/types/Report";
import { assignTaskDates, parseDateString } from "shared/utils/reportScheduleUtils";
import { parseDate } from "shared/utils/dateUtils";

export interface ReleaseEntry {
    table: string;
    id?: string;
    data: Record<string, unknown>;
}

interface HomeworkReleaseInput {
    collection: "ReportAssignLesson" | "ReportAssignStudent";
    targetIds: string[];
    selectedItems: SelectedItem[];
    schedules: HomeworkSchedule[];
    startDate: string;
    reportSetting?: setting;
}

const isDocumentId = (value: string) => Boolean(value.trim()) && !value.includes("/");

export const buildBranchReleaseEntries = (
    targetIds: string[], selectedItems: SelectedItem[], effectiveDate: { start: string; end: string },
): ReleaseEntry[] => {
    if (!targetIds.length || targetIds.some(id => !isDocumentId(id))) throw new Error("배포할 학원을 선택해주세요.");
    if (!selectedItems.length) throw new Error("배포할 과제를 선택해주세요.");
    if (!parseDateString(effectiveDate.start) || !parseDateString(effectiveDate.end) || effectiveDate.start > effectiveDate.end) {
        throw new Error("배포 시작일과 종료일을 확인해주세요.");
    }
    if (selectedItems.some(item => !isDocumentId(item.id) || !item.path || !item.start)) {
        throw new Error("과제 경로를 확인해주세요. 과제를 다시 선택해주세요.");
    }
    return Array.from(new Set(targetIds)).sort().flatMap(branchId => [...selectedItems].sort((a, b) => a.id.localeCompare(b.id)).map(report => ({
        table: `ReportAssignBranch/${branchId}/Report`,
        // Branch grants are updated in place; retain the existing document IDs.
        id: report.id,
        data: {
            report: { id: report.id, path: report.path, start: report.start },
            effective_date: { ...effectiveDate },
        },
    })));
};

/** Validate the entire selection before creating any writes. */
export const validateHomeworkSchedules = (
    selectedItems: SelectedItem[], schedules: HomeworkSchedule[],
): void => {
    if (!selectedItems.length) throw new Error("배포할 과제를 선택해주세요.");
    const selectedIds = new Set(selectedItems.map(item => item.id));
    const scheduledIds = new Set(schedules.map(schedule => schedule.id));
    if (selectedIds.size !== selectedItems.length || scheduledIds.size !== schedules.length ||
        schedules.length !== selectedItems.length || schedules.some(schedule => !selectedIds.has(schedule.id))) {
        throw new Error("선택한 모든 과제의 일정을 다시 설정해주세요.");
    }
    for (const item of selectedItems) {
        const pathParts = item.path.split("/");
        if (!isDocumentId(item.id) || pathParts.length % 2 !== 1 || pathParts.some(part => !part.trim())) {
            throw new Error("과제 경로가 올바르지 않습니다. 과제를 다시 선택해주세요.");
        }
    }
    for (const schedule of schedules) {
        if (!Number.isInteger(schedule.order) || schedule.order < 0) {
            throw new Error("과제 순서를 다시 설정해주세요.");
        }
        if (schedule.dates?.length) {
            if (schedule.dates.some(date => !parseDateString(date))) {
                throw new Error("모든 과제에 올바른 날짜를 지정해주세요.");
            }
        } else if (schedule.dayOfWeek.length !== 7 ||
            !schedule.dayOfWeek.every(day => typeof day === "boolean") ||
            !schedule.dayOfWeek.some(Boolean) || !Number.isInteger(schedule.interval) || schedule.interval < 1) {
            throw new Error("모든 과제에 요일과 올바른 주기를 지정해주세요.");
        }
    }
};

export const hasCompleteHomeworkSchedules = (items: SelectedItem[], schedules: HomeworkSchedule[]): boolean => {
    try {
        validateHomeworkSchedules(items, schedules);
        return true;
    } catch {
        return false;
    }
};

export const buildHomeworkReleaseEntries = (input: HomeworkReleaseInput): ReleaseEntry[] => {
    const { collection, selectedItems, schedules, startDate, reportSetting } = input;
    if (!input.targetIds.length || input.targetIds.some(id => !isDocumentId(id))) {
        throw new Error(collection === "ReportAssignLesson" ? "배포할 클래스를 선택해주세요." : "배포할 학생을 선택해주세요.");
    }
    validateHomeworkSchedules(selectedItems, schedules);
    if (!parseDateString(startDate)) throw new Error("올바른 시작일을 지정해주세요.");

    const assignedDates = new Map<string, string[]>();
    const weeklyGroups = new Map<string, HomeworkSchedule[]>();
    for (const schedule of schedules) {
        if (schedule.dates?.length) {
            assignedDates.set(schedule.id, Array.from(new Set(schedule.dates)).sort());
        } else {
            const key = `${schedule.interval}:${schedule.dayOfWeek.join(",")}`;
            weeklyGroups.set(key, [...(weeklyGroups.get(key) || []), schedule]);
        }
    }
    weeklyGroups.forEach(group => {
        const sorted = [...group].sort((a, b) => a.order - b.order);
        const assigned = assignTaskDates(sorted, sorted[0].dayOfWeek, startDate, sorted[0].interval);
        if (assigned.length !== group.length) throw new Error("과제 일정을 계산하지 못했습니다. 일정을 다시 설정해주세요.");
        assigned.forEach(schedule => assignedDates.set(schedule.id, [parseDate(schedule.assignedDate)]));
    });

    const allDates = Array.from(assignedDates.values()).flat().sort();
    // The end date input is hidden in these screens. Derive the range from the
    // actual assignments so future homework is not stored with an expired range.
    const effectiveDate = { start: allDates[0], end: allDates[allDates.length - 1] };
    const entries: ReleaseEntry[] = [];
    for (const targetId of Array.from(new Set(input.targetIds)).sort()) {
        for (const schedule of [...schedules].sort((a, b) => a.order - b.order)) {
            const item = selectedItems.find(selected => selected.id === schedule.id)!;
            for (const date of assignedDates.get(schedule.id)!) {
                entries.push({
                    table: `${collection}/${targetId}/Report`,
                    data: {
                        effective_date: effectiveDate,
                        date,
                        ...(schedule.dates?.length ? {} : { dayOfWeek: [...schedule.dayOfWeek], interval: schedule.interval }),
                        order: schedule.order,
                        report: { id: item.id, path: item.path, order: schedule.order },
                        report_setting: reportSetting || null,
                    },
                });
            }
        }
    }
    return entries;
};
